import React, { useCallback } from "react";
import { Formik, useFormikContext } from "formik";
import { Box, Typography } from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { StepsButton } from "./StepsButtons";
import { setActiveStep } from "../redux/stepperSlice";
import store, { AppDispatch, RootReducerType } from "@store/store";
import { toast } from "react-toastify";
import {
  UtilityFormRendererRetail,
  Field,
  sectionsRetail,
  sectionPrivate,
  Section,
} from "@modules/stepper/components/AccessFormData";
import { setSiteRoadwaysField } from "../redux/siteRoadwaysSlice";
import { submitAmenitiesThunk } from "../redux/thunk/amenitiesThunk";
import {
  ifHillySectionRetailNew,
  ifRuralSectionPrivateCommonNew,
  ifRuralSectionPrivateNoServiceNew,
  ifRuralSectionRetailNew,
  ifUrbanSectionPrivateCommonNew,
  ifUrbanSectionPrivateNoServiceNew,
  ifUrbanSectionRetailNew,
  // Residential Properties
  ifResidentialUrbanSectionPrivateCommonNew,
  ifResidentialUrbanSectionPrivateNoServiceNew,
  ifResidentialRuralSectionPrivateCommonNew,
  ifResidentialRuralSectionPrivateNoServiceNew,
  // Other Properties
  ifOtherUrbanSectionPrivateCommonNew,
  ifOtherUrbanSectionPrivateNoServiceNew,
  ifOtherRuralSectionPrivateCommonNew,
  ifOtherRuralSectionPrivateNoServiceNew,
} from "./data";
import { AmenitiesSectionProps } from "../types";

// --- Helper Types ---
type YesNoQuestion = {
  id: number;
  name: string;
  label: string;
};

const getYesNoQuestions = (sections: any[]): YesNoQuestion[] => {
  const yesNo: YesNoQuestion[] = [];
  const traverse = (field: Field) => {
    if (field.type === "radio" && field.id !== undefined) {
      yesNo.push({
        id: Number(field.id),
        name: field.name.toString(),
        label: field.title,
      });
    }
    if (field.nestedFields && Array.isArray(field.nestedFields)) {
      field.nestedFields.forEach(traverse);
    }
  };
  sections.forEach((section) => section.fields.forEach(traverse));
  return yesNo;
};

// --- Helper to collect all visible fields based on current values ---
const collectVisibleFields = (
  sections: Section[],
  formValues: any
): Field[] => {
  const visibleFields: Field[] = [];

  const isFieldVisible = (field: Field): boolean => {
    if (!field.showIf) return true;
    const conditionField = field.showIf.field.toString();
    const conditionValue = field.showIf.value;
    const currentValue = formValues[conditionField];

    // Handle type coercion for numeric comparisons
    // service_road_type_id and other select fields might be stored as string or number
    let isVisible = false;
    if (typeof conditionValue === 'number' && typeof currentValue === 'string') {
      isVisible = Number(currentValue) === conditionValue;
    } else if (typeof conditionValue === 'string' && typeof currentValue === 'number') {
      isVisible = conditionValue === String(currentValue);
    } else {
      isVisible = currentValue === conditionValue;
    }

    return isVisible;
  };

  const traverseFields = (fields: Field[], depth = 0) => {
    const indent = '  '.repeat(depth);
    fields.forEach((field) => {
      // Only include field if it passes the showIf condition
      if (isFieldVisible(field)) {
        visibleFields.push(field);

        // Check nested fields
        if (field.nestedFields && field.nestedFields.length > 0) {
          const fieldValue = formValues[field.name];
          // Nested fields are visible if:
          // 1. Parent has alwaysShowNested, OR
          // 2. Parent is a radio with value "Yes", OR  
          // 3. Parent is a select/other with a value
          const shouldShowNested = field.alwaysShowNested ||
            fieldValue === "Yes" ||
            (fieldValue !== "" && fieldValue !== undefined && field.type !== "radio");

          if (shouldShowNested) {
            traverseFields(field.nestedFields, depth + 1);
          }
        }
      }
    });
  };

  sections.forEach((section, idx) => {
    if (section.fields) {
      traverseFields(section.fields);
    }
  });

  return visibleFields;
};

// --- Helper function to create dynamic validation schema ---
const createDynamicValidationSchema = (
  dynamicSections: Section[],
  category_id: number,
  formValues: any
) => {
  const shape: Record<string, any> = {};

  // Get all currently visible fields
  const visibleFields = collectVisibleFields(dynamicSections, formValues);

  // Check specific conditional fields
  const conditionalFields = ['on_slip_road', 'access_criteria_met', 'is_constructed_without_prior_permission', 'is_facility_operational'];

  conditionalFields.forEach(fieldName => {
    const isVisible = visibleFields.some(f => f.name === fieldName);
    const hasValue = formValues[fieldName];
    const isEmpty = hasValue === '' || hasValue === undefined || hasValue === null;
  });


  // Create validation only for visible fields
  visibleFields.forEach((field) => {
    const fieldName = field.name.toString();

    if (field.type === "text" || field.type === "date") {
      if (field.dataType === "number") {
        shape[fieldName] = Yup.number()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .typeError("Must be a number")
          .test(
            'positive-or-zero',
            'Must be positive',
            value => value == null || value === 0 || value > 0
          )
          .required("Required");
      } else {
        shape[fieldName] = Yup.string().required("Required");
      }
    } else if (field.type === "radio") {
      shape[fieldName] = Yup.string()
        .oneOf(["Yes", "No"], "Required")
        .required("Required");
    } else if (field.type === "select") {
      shape[fieldName] = Yup.string().required("Required");
    }
  });

  // Add conditional validation for location_type_id (always required)
  shape.location_type_id = Yup.string().required("Required");

  // Add conditional validation for plot fields based on location type
  // shape.plot_length_m = Yup.number()
  //     .typeError('Must be a number')
  //     .positive('Must be positive')
  //     .required('Required')
  //     .test('min-value', 'Value does not meet minimum requirement', function (value) {
  //         const locationType = this.parent.location_type_id;
  //         if (!locationType || !value) return true;

  //         const minValues = {
  //             '1': 30, // Urban
  //             '2': 35, // Rural
  //             '3': 20, // Hilly
  //         };
  //         return value >= (minValues[String(locationType) as keyof typeof minValues] || 0);
  //     });

  // shape.plot_width_m = Yup.number()
  //     .typeError('Must be a number')
  //     .positive('Must be positive')
  //     .required('Required')
  //     .test('min-value', 'Value does not meet minimum requirement', function (value) {
  //         const locationType = this.parent.location_type_id;
  //         if (!locationType || !value) return true;

  //         const minValues = {
  //             '1': 30, // Urban
  //             '2': 35, // Rural
  //             '3': 20, // Hilly
  //         };
  //         return value >= (minValues[String(locationType) as keyof typeof minValues] || 0);
  //     });

  shape.plot_area = Yup.number()
    .typeError("Must be a number")
    .test(
      'positive-or-zero',
      'Must be positive',
      value => value == null || value === 0 || value > 0
    )
    .required("Required");

  // Add LOI fields validation for retail category
  // LOI fields are only required if COCO fuel station is "No"
  if (category_id === 1) {
    shape.is_coco_fuel_station = Yup.string()
      .oneOf(["Yes", "No"], "Required")
      .required("Required");

    shape.loi_name = Yup.string().when("is_coco_fuel_station", {
      is: "No",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });

    shape.loi_dealer_name = Yup.string().when("is_coco_fuel_station", {
      is: "No",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });

    shape.loi_number = Yup.string().when("is_coco_fuel_station", {
      is: "No",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });

    shape.loi_date = Yup.string().when("is_coco_fuel_station", {
      is: "No",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });
  }

  return Yup.object().shape(shape);
};

// --- Main Component ---
export const SiteRoadwaysAccess: React.FC<AmenitiesSectionProps> = ({
  collapsed,
}) => {
  const { activeStep } = useSelector((state: RootReducerType) => state.stepper);
  const { category_id, access_id, property_type_id } = useSelector(
    (state: RootReducerType) => state.amenities
  );
  const siteRoadways = useSelector(
    (state: RootReducerType) => state.siteRoadways
  );
  const applicationEditId = useSelector(
    (state: RootReducerType) => state.applicationDashboard?.id
  );
  const dispatch = useDispatch<AppDispatch>();
  const baseSections = category_id === 2 ? sectionPrivate : sectionsRetail;

  // Function to get dynamic sections based on location type and access conditions
  const getDynamicSections = useCallback(
    (locationType: number, serviceRoadType: number, formValues: any = {}) => {
      const dynamicSections = JSON.parse(JSON.stringify(baseSections));

      // Check if we should show location-specific sections
      // This matches the child component's shouldShowAccessCriteriaMet logic
      const allFieldsFilled =
        formValues.location_type_id &&
        formValues.plot_area;

      const isServiceRoadProvided = formValues.is_service_road_provided;
      const isServiceListVisible = formValues.service_road_type_id;

      // Handle type coercion for service_road_type_id (might be string or number)
      const normalizedServiceListVisible = typeof isServiceListVisible === 'string'
        ? Number(isServiceListVisible)
        : isServiceListVisible;

      // For Retail: Check is_service_road_provided
      // For Private: Check service_road_type_id
      const shouldShowAccessCriteriaMet = category_id === 2
        ? (allFieldsFilled && (normalizedServiceListVisible === 1 || normalizedServiceListVisible === 2 || normalizedServiceListVisible === 3))
        : ((allFieldsFilled && isServiceRoadProvided === "No") ||
          (allFieldsFilled && (normalizedServiceListVisible === 1 || normalizedServiceListVisible === 2 || normalizedServiceListVisible === 3)));

      // Only include location-specific sections if conditions are met
      if (!shouldShowAccessCriteriaMet) {
        return [...dynamicSections.slice(0, 1)];
      }

      let locationSpecificSections: Section[] = [];

      // Handle type coercion - serviceRoadType might be string or number
      const normalizedServiceRoadType = typeof serviceRoadType === 'string'
        ? Number(serviceRoadType)
        : serviceRoadType;

      // Handle type coercion for property_type_id (might be string or number)
      const propertyTypeIdFromForm = property_type_id;
      const normalizedPropertyTypeId = typeof propertyTypeIdFromForm === 'string'
        ? Number(propertyTypeIdFromForm)
        : propertyTypeIdFromForm;

      if (category_id === 2) {
        // Check if property_type_id is Residential (7, 8, 9) or Other (10, 11, 12, 13, 14)
        const isResidentialProperty = [7, 8, 9].includes(normalizedPropertyTypeId as any);
        const isOtherProperty = [10, 11, 12, 13, 14].includes(normalizedPropertyTypeId as any);

        if (isResidentialProperty) {
          // Residential Properties
          switch (locationType) {
            case 1: // Urban
              if (normalizedServiceRoadType === 1) {
                locationSpecificSections = []; // Existing Slip Road - fields are in base section with showIf
              } else if (normalizedServiceRoadType === 2) {
                locationSpecificSections = ifResidentialUrbanSectionPrivateCommonNew;
              } else if (normalizedServiceRoadType === 3) {
                locationSpecificSections = ifResidentialUrbanSectionPrivateNoServiceNew;
              } else {
                locationSpecificSections = [];
              }
              break;
            case 2: // Rural
              if (normalizedServiceRoadType === 1) {
                locationSpecificSections = []; // Existing Slip Road - fields are in base section with showIf
              } else if (normalizedServiceRoadType === 2) {
                locationSpecificSections = ifResidentialRuralSectionPrivateCommonNew;
              } else if (normalizedServiceRoadType === 3) {
                locationSpecificSections = ifResidentialRuralSectionPrivateNoServiceNew;
              } else {
                locationSpecificSections = [];
              }
              break;
            default:
              locationSpecificSections = [];
          }
        } else if (isOtherProperty) {
          // Other Properties
          switch (locationType) {
            case 1: // Urban
              if (normalizedServiceRoadType === 1) {
                locationSpecificSections = []; // Existing Slip Road - fields are in base section with showIf
              } else if (normalizedServiceRoadType === 2) {
                locationSpecificSections = ifOtherUrbanSectionPrivateCommonNew;
              } else if (normalizedServiceRoadType === 3) {
                locationSpecificSections = ifOtherUrbanSectionPrivateNoServiceNew;
              } else {
                locationSpecificSections = [];
              }
              break;
            case 2: // Rural
              if (normalizedServiceRoadType === 1) {
                locationSpecificSections = []; // Existing Slip Road - fields are in base section with showIf
              } else if (normalizedServiceRoadType === 2) {
                locationSpecificSections = ifOtherRuralSectionPrivateCommonNew;
              } else if (normalizedServiceRoadType === 3) {
                locationSpecificSections = ifOtherRuralSectionPrivateNoServiceNew;
              } else {
                locationSpecificSections = [];
              }
              break;
            default:
              locationSpecificSections = [];
          }
        } else {
          // Fallback: No valid property type
          locationSpecificSections = [];
        }
      } else {
        switch (locationType) {
          case 1:
            locationSpecificSections = ifUrbanSectionRetailNew;
            break;
          case 2:
            locationSpecificSections = ifRuralSectionRetailNew;
            break;
          case 3:
            locationSpecificSections = ifHillySectionRetailNew;
            break;
          default:
            locationSpecificSections = [];
        }
      }
      return [...dynamicSections.slice(0, 1), ...locationSpecificSections];
    },
    [category_id, baseSections]
  );

  // Get initial values and dynamic sections
  const initialLocationType = siteRoadways?.location_type_id;
  const initialServiceRoadType = siteRoadways?.service_road_type_id;
  const initialFormValues = {
    location_type_id: initialLocationType,
    service_road_type_id: initialServiceRoadType,
    property_type_id: siteRoadways?.property_type_id,
    plot_area: siteRoadways?.plot_area,
    is_service_road_provided: siteRoadways?.is_service_road_provided,
  };
  const initialDynamicSections = getDynamicSections(
    Number(initialLocationType),
    Number(initialServiceRoadType),
    initialFormValues
  );
  const initialYesNoQuestions = getYesNoQuestions(initialDynamicSections);

  const question_true = siteRoadways?.question_true ?? [];
  const question_false = siteRoadways?.question_false ?? [];

  const penaltyInitialKey = {
    is_constructed_without_prior_permission_consent: false,
    is_facility_operational_consent: false,
  };

  // Build initial values
  const initialValues: Record<string, any> = {
    ...(category_id !== 2 &&
      (siteRoadways?.is_service_road_provided as any) === "No"
      ? penaltyInitialKey
      : {}),
  };

  // Set initial values for yes/no questions
  initialYesNoQuestions.forEach((q) => {
    if (question_true.includes(q.id)) initialValues[q.name] = "Yes";
    else if (question_false.includes(q.id)) initialValues[q.name] = "No";
    else initialValues[q.name] = "";
  });

  // --- Recursive Utilities ---
  const collectAllFields = (sections: any[]): Field[] => {
    const all: Field[] = [];
    const traverse = (field: Field) => {
      all.push(field);
      field.nestedFields?.forEach(traverse);
    };
    sections.forEach((section) => section.fields.forEach(traverse));
    return all;
  };

  // Set initial values for other fields
  const allFields = collectAllFields(initialDynamicSections);
  allFields.forEach((field) => {
    if (typeof initialValues[field.name] === "undefined") {
      initialValues[field.name] = (siteRoadways as any)?.[field.name] ?? "";
    }
  });

  const backButtonClickHandler = () => {
    dispatch(setActiveStep(activeStep - 1));
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={(values) => {
        // Dynamically generate sections based on current values
        const currentLocationType = values.location_type_id || "";
        const currentServiceRoadType = values.service_road_type_id || "";
        const currentDynamicSections = getDynamicSections(
          currentLocationType,
          currentServiceRoadType,
          values
        );

        // Generate dynamic validation schema based on current sections
        const schema = createDynamicValidationSchema(currentDynamicSections, category_id, values);

        try {
          schema.validateSync(values, { abortEarly: false });
          return {};
        } catch (err: any) {
          const errors: Record<string, string> = {};
          if (err.inner) {
            err.inner.forEach((error: any) => {
              if (error.path) {
                errors[error.path] = error.message;
              }
            });
          }
          return errors;
        }
      }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const currentLocationType = values.location_type_id || "";
          const currentServiceRoadType = values.service_road_type_id || "";
          const latestSections = getDynamicSections(
            currentLocationType,
            currentServiceRoadType,
            values
          );
          const latestYesNoQuestions = getYesNoQuestions(latestSections);

          const question_true: number[] = [];
          const question_false: number[] = [];

          const dynamicSections = getDynamicSections(
            currentLocationType,
            currentServiceRoadType,
            values
          );
          getYesNoQuestions(dynamicSections).forEach(({ id, name }) => {
            const answer = values[name];
            if (answer === "Yes") question_true.push(id);
            else if (answer === "No") question_false.push(id);
          });

          const inputPayload: Record<string, any> = {};
          Object.keys(values).forEach((key) => {
            const isIdQuestion = latestYesNoQuestions.some(
              (q) => q.name === key
            );
            if (!isIdQuestion) {
              const raw = values[key];
              if (raw === "Yes") inputPayload[key] = true;
              else if (raw === "No") inputPayload[key] = false;
              else if (
                raw === "" ||
                raw === undefined ||
                raw === null ||
                raw === 0 ||
                raw === "0"
              ) {
                // skip — do not send blank, null, 0 or "0" values
              } else inputPayload[key] = raw;
            }
          });

          const finalPayload = {
            category_id,
            access_id,
            form_step: 2,
            question_true,
            question_false,
            ...inputPayload,
          };

          const response = await dispatch(submitAmenitiesThunk(finalPayload));
          if (response.payload.success) {
            toast.success("Access Amenities Information saved successfully");
            dispatch(setActiveStep(activeStep + 1));
          } else {
            toast.error(
              response?.payload?.message || "Failed to save application"
            );
          }
        } catch (error) {
          toast.error("An error occurred while submitting the form");
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isValid, setFieldValue, values, validateForm, errors, touched }) => {
        return (
          <InnerForm
            category_id={category_id}
            backButtonClickHandler={backButtonClickHandler}
            baseSections={baseSections}
            getDynamicSections={getDynamicSections}
            handleSubmit={handleSubmit}
            isValid={isValid}
            setFieldValue={setFieldValue}
            values={values}
            validateForm={validateForm}
          />
        );
      }}
    </Formik>
  );
};

interface InnerFormProps {
  category_id: number;
  backButtonClickHandler: () => void;
  baseSections: any[];
  getDynamicSections: (locationType: any, serviceRoadType: any, formValues?: any) => any[];
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  isValid: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  values: any;
  validateForm: (values?: any) => Promise<any>;
}

const InnerForm: React.FC<InnerFormProps> = ({
  category_id,
  backButtonClickHandler,
  baseSections,
  getDynamicSections,
  handleSubmit,
  isValid,
  setFieldValue,
  values,
  validateForm,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  // Get current dynamic sections based on location type
  const currentLocationType = values.location_type_id || "";
  const currentServiceRoadType = values.service_road_type_id || "";
  const dynamicSections = getDynamicSections(
    currentLocationType,
    currentServiceRoadType,
    values
  );

  // Track previous sections to trigger validation when they change
  const prevSectionsRef = React.useRef<string>("");

  React.useEffect(() => {
    // Create a string representation of sections to compare
    const sectionsString = JSON.stringify(dynamicSections.map((s) => s.title));

    if (sectionsString !== prevSectionsRef.current && prevSectionsRef.current !== "") {
      validateForm();
    }
    prevSectionsRef.current = sectionsString;
  }, [dynamicSections, validateForm, values.service_road_type_id, values.location_type_id]);

  // Trigger validation whenever any value changes that might affect field visibility
  React.useEffect(() => {
    // Debounce full-form validation so typing stays responsive
    const timer = setTimeout(() => {
      validateForm();
    }, 300);
    return () => clearTimeout(timer);
  }, [values, validateForm]);

  return (
    <Box className="amenities-container" component="div">
      <Box className="form-wrapper" sx={{ paddingTop: 0 }}>
        <form onSubmit={handleSubmit}>
          <UtilityFormRendererRetail
            sections={dynamicSections}
            handleFieldChange={(name, value) => {
              setFieldValue(String(name), value, true); // Enable validation on field change
              dispatch(setSiteRoadwaysField({ name: String(name), value }));

              // Clear LOI fields when is_coco_fuel_station is set to "Yes"
              if (String(name) === 'is_coco_fuel_station' && value === 'Yes') {
                setFieldValue('loi_name', '');
                setFieldValue('loi_dealer_name', '');
                setFieldValue('loi_number', '');
                setFieldValue('loi_date', '');
                dispatch(setSiteRoadwaysField({ name: 'loi_name', value: '' }));
                dispatch(setSiteRoadwaysField({ name: 'loi_dealer_name', value: '' }));
                dispatch(setSiteRoadwaysField({ name: 'loi_number', value: '' }));
                dispatch(setSiteRoadwaysField({ name: 'loi_date', value: '' }));
              }

              // Clear hidden fields when is_service_road_provided changes
              if (String(name) === 'is_service_road_provided') {
                if (value === 'No') {
                  // Clear Yes-dependent fields
                  setFieldValue('on_slip_road', '');
                  setFieldValue('access_criteria_met', '');
                  dispatch(setSiteRoadwaysField({ name: 'on_slip_road', value: '' }));
                  dispatch(setSiteRoadwaysField({ name: 'access_criteria_met', value: '' }));
                } else if (value === 'Yes') {
                  // Clear No-dependent fields
                  setFieldValue('is_constructed_without_prior_permission', '');
                  setFieldValue('is_facility_operational', '');
                  dispatch(setSiteRoadwaysField({ name: 'is_constructed_without_prior_permission', value: '' }));
                  dispatch(setSiteRoadwaysField({ name: 'is_facility_operational', value: '' }));
                }
              }

              // Update radio questions true/false arrays dynamically
              const updatedQuestionsTrue: number[] = [];
              const updatedQuestionsFalse: number[] = [];
              getYesNoQuestions(dynamicSections).forEach(({ id, name: qName }) => {
                const answer = String(name) === String(qName) ? value : values[qName];
                if (answer === "Yes") updatedQuestionsTrue.push(id);
                else if (answer === "No") updatedQuestionsFalse.push(id);
              });
              dispatch(
                setSiteRoadwaysField({
                  name: "question_true",
                  value: updatedQuestionsTrue,
                })
              );
              dispatch(
                setSiteRoadwaysField({
                  name: "question_false",
                  value: updatedQuestionsFalse,
                })
              );
            }}
          />
        </form>
      </Box>
      <Box className="stepper-btns gray:br-t-500 mt-3">
        <StepsButton
          onBack={backButtonClickHandler}
          onNext={handleSubmit}
          nextDisabled={!isValid}
        />
      </Box>
    </Box>
  );
};
